import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import {
    MediumFacade,
    MediumType,
    ProductsFacade,
    PurchaseFacade,
    RelatedCardFacade,
    ShoppingCartFacade
} from '@ridango/components';
import { CoreFacade, Language, Product } from '@ridango/core';
import Env from '@ridango/core/env';

import { combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'gl-bike-garage-page',
    templateUrl: './bike-garage-page.component.html',
    styleUrls: ['./bike-garage-page.component.scss']
})
export class BikeGaragePageComponent implements OnInit, OnDestroy {
    private componentDestroyed$ = new Subject<void>();
    private CARD_TYPE_MASK = 4;

    steps$: Observable<any[]> = this.purchaseFacade.purchaseSteps$;
    activeStep$: Observable<number> = this.purchaseFacade.activePurchaseStep$;
    isFlowCompleted$: Observable<boolean> = this.purchaseFacade.isFlowComplete$;
    purchase$ = this.purchaseFacade.purchase$;
    payloads$: Observable<any[]> = this.purchaseFacade.payloads$;

    allProducs$ = this.productsFacade.products$;
    allProductIds$ = this.productsFacade.allIds$;
    selectedProduct$ = this.productsFacade.selected$;
    productsLoading$ = this.productsFacade.loading$;

    selectedRelatedCard$ = this.relatedCardFacade.selectedCard$;
    selectedMedium$ = this.mediumFacade.medium$;
    ticketPayloadsPosted$: Observable<boolean> = this.shoppingCartFacade.ticketPayloadsPosted$;
    cart$ = this.shoppingCartFacade.cart$;

    selectedLanguage$: Observable<Language> = this.coreFacade.selectedLanguage$;
    selectedLanguageCode = this.translate.currentLang;

    constructor(
        @Inject('ENV') public env: Env,
        private purchaseFacade: PurchaseFacade,
        private productsFacade: ProductsFacade,
        private mediumFacade: MediumFacade,
        private translate: TranslateService,
        private relatedCardFacade: RelatedCardFacade,
        private shoppingCartFacade: ShoppingCartFacade,
        private coreFacade: CoreFacade,
        private router: Router
    ) {}

    ngOnInit() {
        this.purchaseFacade.setWalletPurchaseSteps([
            'TICKET_TEXT_ADD_DATA',
            'TICKET_TEXT_PAYMENT',
            'TICKET_TEXT_TICKETS'
        ]);
        this.purchaseFacade.setRegionId(this.env.REGION_ID);
        this.purchaseFacade.setActiveWalletPurchaseStep(1);
        this.purchaseFacade.setChannel();
        this.allProductIds$.pipe(takeUntil(this.componentDestroyed$)).subscribe((productIds: any[]) => {
            if (productIds.length > 0) {
                this.allProducs$.pipe(take(1)).subscribe((products: Product[]) => {
                    const filteredProducts = products
                        .filter((product: Product) => {
                            return (
                                product.product_properties.filter((pp) => pp.code === 'BIKE_GARAGE_PRODUCT').length > 0
                            );
                        })
                        .sort((p1, p2) => (p1.visual_order > p2.visual_order ? 1 : -1));
                    this.productsFacade.setFilteredProducts(filteredProducts);
                });
            } else {
                this.productsFacade.fetchPurchaseProducts();
            }
        });
        // as this is known product will set the card type mask here
        this.mediumFacade.setCardTypeMask(this.CARD_TYPE_MASK);
        this.selectedProduct$.pipe(takeUntil(this.componentDestroyed$)).subscribe((product) => {
            if (product) {
                this.purchaseFacade.setSelectedProduct(product);
                this.mediumFacade.setCardTypeMask(product.card_type_mask);
            }
        });
        this.selectedRelatedCard$.pipe(takeUntil(this.componentDestroyed$)).subscribe((card: any) => {
            if (card) {
                this.mediumFacade.setRelatedCardMedium(card);
            }
        });

        this.selectedMedium$.pipe(takeUntil(this.componentDestroyed$)).subscribe((medium) => {
            this.purchaseFacade.setWalletSelectedMedium(medium);
        });
        combineLatest([this.payloads$])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([payloads]) => {
                this.purchaseFacade.setFlowStatus(this.isFlowValid(payloads));
            });

        this.ticketPayloadsPosted$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isPayloadsPosted) => {
            if (isPayloadsPosted) {
                this.cart$.pipe(take(1)).subscribe((c) => {
                    this.shoppingCartFacade.updateCart(c.shopping_cart_id);
                    this.purchaseFacade.setPurchaseShoppingCartId(c.shopping_cart_id);
                    // scroll window to top and navigate to cart
                    window.scroll(0, 0);
                    this.router.navigate([this.translate.currentLang, 'purchase', 'cart']);
                });
            }
        });

        this.selectedLanguage$.pipe(takeUntil(this.componentDestroyed$)).subscribe((l: Language) => {
            if (l?.code) {
                this.selectedLanguageCode = l.code;
            }
        });
    }
    ngOnDestroy() {
        // Clean up selections
        this.purchaseFacade.clearWalletPurchaseSelections();
        this.productsFacade.setSelectedProduct({ product_id: null });
        this.relatedCardFacade.setSelectedRelatedCard(null);
        this.mediumFacade.clear();
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    postToCart() {
        // passing in entire flow, as there are required data that needs to be attached to tickets
        this.purchase$.pipe(take(1)).subscribe((flow) => {
            this.shoppingCartFacade.postPayloads(flow);
        });
    }

    /**
     * Converts payloads to local ticket objects.
     *
     * Is required as our ticket component expects certain feilds to be present.
     *
     * @param {*} payload
     * @return {*}
     * @memberof WalletLoadingPageComponent
     */
    convertPayloadsToLocalTickets(payloads) {
        return payloads.map((payload) => {
            const ticket = { ...payload };
            combineLatest([this.selectedRelatedCard$, this.selectedProduct$, this.selectedMedium$, this.purchase$])
                .pipe(take(1))
                .subscribe(([relatedCard, selectedProduct, selectedMedium, purchase]) => {
                    ticket.product = { ...selectedProduct };
                    if (selectedMedium.type === MediumType.RelatedCard) {
                        ticket.related_card_object = { ...relatedCard };
                    }
                    if (ticket.price) {
                        ticket.product.price = ticket.price;
                    }
                    if (purchase.area) {
                        ticket.area = { ...purchase.area };
                    }
                });
            return ticket;
        });
    }

    isFlowValid(payloads) {
        // no payloads, flow incomplete
        if (payloads.length === 0) {
            return false;
        }

        // check if product is present in payloads
        if (payloads.filter((p) => !p.product_id).length > 0) {
            return false;
        }
        // check if medium is present in payloads
        if (
            payloads.filter((p) => {
                return !p.card_id && !p.related_card_id && !p.email;
            }).length > 0
        ) {
            return false;
        }

        // check if card tpye matches selected product mask in payloads (not sure if needed currently)
        return true;
    }
}
